/**
 * A strategy's definition, as an input that cannot hang the page.
 *
 * **The textarea is gone from the default view, and that is the whole change.** It used to be
 * always-on and always-full: `DslStrategyForm` and `PortfolioStrategyForm` each rendered
 * `<textarea value={definition} rows={16}>`, so uploading a real Composer export put thirty
 * million characters into the DOM and the tab stopped responding. Uploading is how definitions
 * actually arrive here — nobody types a 30 MB minified export — so the editor was paying its worst
 * cost for its rarest use.
 *
 * **Editing survives, behind a disclosure.** Deleting the textarea outright would have removed the
 * only way to author a strategy by typing, and to fix a typo without going back to a file. So it
 * opens on request, for definitions small enough that opening it is safe, and refuses with a
 * reason for the ones that are not. The default view is a summary: what is loaded, how big it is,
 * and the first few hundred characters so you can tell it is the right thing.
 *
 * **The text is not fetched until the editor opens (#418).** The summary needed the whole
 * definition to say how big it was, so opening a strategy transferred 60 MB to render a size and
 * four hundred characters — the same transfer the strategy list had just stopped making (#417).
 * The server now reports the length and the excerpt with the strategy; `stored` carries them, and
 * `onLoadStored` fetches the text when — and only when — somebody opens the inline editor on a
 * definition small enough to be edited. A definition too large to edit is never fetched here at
 * all; uploading a replacement is how it changes, as before.
 *
 * Shared by both source modes because the hazard is the text, not the language — a portfolio
 * document is JSON and a strategy is CLJ, and a textarea does not care which one is too long.
 */
import { useEffect, useState } from 'react';
import { Loader2, Pencil, X } from 'lucide-react';
import { DefinitionFileInput } from './DefinitionFileInput';
import { EXCERPT_CHARS, describeChars, isTooManyCharsToEdit } from '../definitionText';

/** A definition the server holds and the browser does not: its length, and how it starts. */
export interface StoredDefinition {
  chars: number;
  excerpt: string;
}

interface Props {
  id: string;
  /** "Definition" or "Portfolio JSON" — what this text IS, in the author's words. */
  label: string;
  /** Passed to the file picker's `accept`, so it offers the right files first. */
  accept: string;
  uploadLabel: string;
  value: string;
  /**
   * Set while the definition is on the server and not in `value` (#418). Null once it has been
   * fetched or replaced, and always null while creating — then `value` is the whole truth.
   */
  stored?: StoredDefinition | null;
  /** Fetch the stored definition into `value`. Awaited before the inline editor opens. */
  onLoadStored?: () => Promise<unknown>;
  disabled?: boolean;
  placeholder?: string;
  /** Shown beside the size when a check is in flight (portfolio validation is slow). */
  busyNote?: React.ReactNode;
  /** What this definition is, and what validating it will tell you. */
  help: React.ReactNode;
  onChange: (text: string) => void;
}

export function DefinitionInput({
  id,
  label,
  accept,
  uploadLabel,
  value,
  stored,
  onLoadStored,
  disabled,
  placeholder,
  busyNote,
  help,
  onChange,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [loadingStored, setLoadingStored] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loaded = !stored;
  const chars = stored ? stored.chars : value.length;
  const excerpt = stored ? stored.excerpt : value.slice(0, EXCERPT_CHARS);
  const tooLarge = isTooManyCharsToEdit(chars);
  const empty = stored ? chars === 0 : value.trim() === '';

  // A file big enough to close the editor must actually close it — otherwise uploading a 30 MB
  // export while the textarea happened to be open would hang the page anyway, which is the exact
  // failure this component exists to prevent.
  useEffect(() => {
    if (tooLarge) setEditing(false);
  }, [tooLarge]);

  const toggleEditor = async () => {
    if (editing) {
      setEditing(false);
      return;
    }
    if (!loaded && onLoadStored) {
      setLoadingStored(true);
      setLoadError(null);
      try {
        await onLoadStored();
      } catch (caught) {
        setLoadError(caught instanceof Error ? caught.message : 'Could not load the definition');
        return;
      } finally {
        setLoadingStored(false);
      }
    }
    setEditing(true);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        <div className="flex flex-wrap items-center gap-2">
          <DefinitionFileInput
            id={`${id}-file`}
            label={uploadLabel}
            accept={accept}
            disabled={disabled}
            onLoad={onChange}
          />
          <button
            type="button"
            disabled={disabled || tooLarge || loadingStored}
            title={
              tooLarge
                ? `${describeChars(chars)} is too large to edit in the browser. Upload a replacement instead.`
                : undefined
            }
            onClick={toggleEditor}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            {editing ? (
              <>
                <X className="h-4 w-4" aria-hidden="true" /> Close editor
              </>
            ) : loadingStored ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
              </>
            ) : (
              <>
                <Pencil className="h-4 w-4" aria-hidden="true" /> {empty ? 'Write inline' : 'Edit inline'}
              </>
            )}
          </button>
        </div>
      </div>

      {editing ? (
        <textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={16}
          spellCheck={false}
          disabled={disabled}
          placeholder={placeholder}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-xs disabled:bg-slate-50"
        />
      ) : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          {empty ? (
            <p className="text-xs text-slate-500">
              Nothing loaded. Upload a file, or write one inline.
            </p>
          ) : (
            <>
              <p className="text-xs font-medium text-slate-600">
                {describeChars(chars)} {loaded ? 'loaded' : 'stored'}
                {tooLarge && (
                  <span className="ml-2 font-normal text-amber-800">
                    too large to edit here — upload a replacement to change it
                  </span>
                )}
              </p>
              {/* A few hundred characters, so an author can see WHICH definition is loaded without
                  the page paying to lay out the rest of it — or, since #418, to fetch it. */}
              <pre className="mt-1.5 max-h-24 overflow-hidden whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed text-slate-500">
                {excerpt}
                {chars > excerpt.length && '…'}
              </pre>
            </>
          )}
        </div>
      )}

      {loadError && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
          {loadError}
        </p>
      )}

      <p className="flex items-center gap-2 text-xs text-slate-500">
        <span>{help}</span>
        {busyNote && <span className="ml-auto shrink-0 text-slate-400">{busyNote}</span>}
      </p>
    </div>
  );
}
